import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { NotificationBanner } from '@/components/NotificationBanner';

interface NotificationContextType {
	showNotification: (message: string, type?: 'info' | 'warning' | 'success', button?: React.ReactNode) => void;
	hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
	const [notification, setNotification] = useState<{ message: string; type: 'info' | 'warning' | 'success'; button?: React.ReactNode } | null>(null);
	const [notificationHeight, setNotificationHeight] = useState<number>(0);
	const notificationRef = useRef<HTMLDivElement>(null);

	const updateNotificationHeight = () => {
		if (notification && notificationRef.current) {
			const height = notificationRef.current.offsetHeight;
			setNotificationHeight(height);
			document.documentElement.style.setProperty('--notification-height', `${height}px`);
		} else {
			setNotificationHeight(0);
			document.documentElement.style.setProperty('--notification-height', '0px');
		}
	};

	useEffect(() => {
		updateNotificationHeight();

		const handleResize = () => {
			updateNotificationHeight();
		};

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [notification]);

	const showNotification = (message: string, type: 'info' | 'warning' | 'success' = 'info', button?: React.ReactNode) => {
		setNotification({ message, type, button });
	};

	const hideNotification = () => {
		setNotification(null);
	};

	return (
		<NotificationContext.Provider value={{ showNotification, hideNotification }}>
			{notification && (
				<div
					className="fixed top-0 left-0 right-0 z-50"
					ref={notificationRef}
				>
					<NotificationBanner
						message={notification.message}
						type={notification.type}
						button={notification.button}
						onClose={hideNotification}
					/>
				</div>
			)}
			{children}
		</NotificationContext.Provider>
	);
};

export const useNotification = () => {
	const context = useContext(NotificationContext);
	if (context === undefined) {
		throw new Error('useNotification must be used within a NotificationProvider');
	}
	return context;
};
